import { useMemo } from 'react';
import { orderBy, where } from 'firebase/firestore';
import { useCollection } from 'core/hooks/useCollection';
import { IDeliveryRequestDao } from 'core/api/types/delivery-requests.interface';
import { DeliveryRequestsApiService } from 'core/api';

/**
 * The holding area: requests marked for a decision, but not yet committed.
 *
 * Selected on the mark itself and ordered by arrival, the same as every other
 * view of these requests. Ordering by when the mark was made would have
 * selected them too — Firestore leaves out documents missing the field an
 * ordering names — but re-marking rewrites that timestamp, so changing a mark
 * moved the row, which is disorienting in a list someone is reading.
 *
 * Asked of the database rather than filtered out of the loaded page, for the
 * same reason as the waiting count: the screen loads a page at a time, so a
 * queue counted in the browser would stop at a page and understate itself.
 */

/** The queue is meant to be worked down, so it should never approach this. */
export const MARKED_LIMIT = 99;

/** The marks a request can carry. Anything carrying one is in the queue. */
const MARK_DECISIONS = ['accept', 'decline'];

export const useMarkedRequests = () => {
  const constraints = useMemo(
    () => [where('pendingDecision.decision', 'in', MARK_DECISIONS), orderBy('created.at', 'desc')],
    [],
  );

  const { data } = useCollection<IDeliveryRequestDao>({
    apiService: DeliveryRequestsApiService,
    constraints,
    initialLimit: MARKED_LIMIT,
  });

  const requests = data ?? [];
  return {
    requests,
    count: requests.length,
    /** True when there may be more marked than the subscription carries. */
    capped: requests.length >= MARKED_LIMIT,
    label: requests.length >= MARKED_LIMIT ? `${MARKED_LIMIT}+` : String(requests.length),
  };
};
